/**
 * as-promo-code.js — R12 Cart Promo Code (with Dynamic Threshold Validation & Auto-Removal)
 * ES2022 · Defines the <cart-coupon-discount> custom element.
 *
 *  · In-place dynamic cart section refresh (No full page reload).
 *  · Dynamic threshold validation for any free shipping code (FREESHIP50, FREESHIP60, ECOFREESHIP, etc.).
 *  · Automatic code removal when cart subtotal drops below required threshold.
 *  · Uses existing theme free shipping visual design on the shipping row ($9.95 FREE).
 */

"use strict";

(() => {
  const APPLIED_KEY = "as_promo_applied";

  const money = (cents) =>
    "$" + (cents / 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  function validateCode(raw) {
    if (!raw || raw.trim().length < 2) return "Please enter a promo code.";
    if (raw.length > 40) return "Promo code is too long (max 40 characters).";
    if (!/^[A-Z0-9_\-]+$/i.test(raw)) return "Code may only contain letters, numbers, - and _.";
    return null;
  }

  function getFreeShippingThreshold(codeStr) {
    if (!codeStr) return null;
    const upper = codeStr.toUpperCase().trim();
    if (/FREE|SHIP|FS/i.test(upper)) {
      const match = upper.match(/(\d+)/);
      if (match && match[1]) {
        const val = parseInt(match[1], 10);
        if (!isNaN(val) && val > 0) return val;
      }
      if (window.free_shipping_price) {
        return parseFloat(window.free_shipping_price);
      }
    }
    return null;
  }

  class CartCouponDiscount extends HTMLElement {
    /* Fast, live DOM element accessors */
    get _toggle() { return this.querySelector("[data-coupon-toggle]"); }
    get _fieldWrap() { return this.querySelector("[data-coupon-field]"); }
    get _input() { return this.querySelector("[data-coupon-input]"); }
    get _applyBtn() { return this.querySelector(".as-coupon-apply-btn"); }
    get _errorEl() { return this.querySelector(".as-coupon-error"); }
    get _serverChip() { return this.querySelector("[data-coupon-chip]"); }
    get _clientChip() { return this.querySelector("[data-client-chip]"); }

    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      this._busy = false;

      // Delegated event handling
      this.addEventListener("click", (e) => {
        const hit = (sel) => e.target.closest?.(sel);
        if (hit("[data-coupon-toggle]")) {
          this._toggleField();
        } else if (hit(".as-coupon-apply-btn")) {
          this._handleApply();
        } else if (hit("[data-coupon-cancel]")) {
          this._handleCancel();
        } else if (hit("[data-coupon-change], [data-client-change]")) {
          this._showChangeField();
        } else if (hit("[data-coupon-remove], [data-client-remove]")) {
          this._handleRemove(hit("[data-coupon-remove], [data-client-remove]"));
        }
      });

      this.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && e.target.matches?.("[data-coupon-input]")) {
          e.preventDefault();
          this._handleApply();
        }
      });

      this.addEventListener("input", (e) => {
        if (e.target.matches?.("[data-coupon-input]")) {
          this._clearError();
          this._forceUppercase(e.target);
        }
      });

      window.addEventListener("pageshow", (e) => {
        if (e.persisted) this._resyncFromCart();
      });

      // Listen for external cart item changes / removals to auto-validate threshold
      const resyncHandler = () => this._resyncFromCart();
      ["cart:updated", "cart-update", "cart:change", "cart:refresh"].forEach((evt) => {
        document.addEventListener(evt, resyncHandler);
      });

      this._restoreState();
    }

    /* ── Cart-level discount helper (derived) ─────────────────────────────── */
    _cartLevelDiscountTotal(cart) {
      if (!cart) return 0;
      if (Array.isArray(cart.cart_level_discount_applications)) {
        const manualApps = cart.cart_level_discount_applications.filter(app => app.type === "discount_code");
        if (manualApps.length > 0) {
          let codeTotal = 0;
          manualApps.forEach((app) => {
            codeTotal += (app.total_allocated_amount || 0);
          });
          return codeTotal;
        }
        let total = 0;
        cart.cart_level_discount_applications.forEach((app) => {
          total += (app.total_allocated_amount || 0);
        });
        if (total > 0) return total;
      }
      const totalDiscountCents = cart.total_discount || 0;
      let itemLevelCents = 0;
      (cart.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((alloc) => {
          itemLevelCents += alloc.amount || 0;
        });
      });
      return Math.max(0, totalDiscountCents - itemLevelCents);
    }

    /* ── UI Field Toggle & State ─────────────────────────────────────────── */
    _toggleField() {
      const open = this._toggle?.getAttribute("aria-expanded") === "true";
      this._setFieldOpen(!open);
      if (!open) this._input?.focus();
    }

    _setFieldOpen(open) {
      this._toggle?.setAttribute("aria-expanded", String(open));
      this._fieldWrap?.classList.toggle("open", open);
    }

    _forceUppercase(input) {
      const el = input ?? this._input;
      if (!el) return;
      const pos = el.selectionStart;
      el.value = el.value.toUpperCase();
      el.setSelectionRange(pos, pos);
    }

    _ensureSomethingVisible() {
      if (this._serverChip || this._clientChip) return;
      if (this._toggle) this._toggle.hidden = false;
      else this._setFieldOpen(true);
    }

    _handleCancel() {
      if (this._input) this._input.value = "";
      this._clearError();
      this._setFieldOpen(false);
      this._ensureSomethingVisible();
    }

    /* ── Apply Coupon Code ───────────────────────────────────────────────── */
    async _handleApply() {
      if (this._busy) return;
      const raw = (this._input?.value ?? "").trim();
      const err = validateCode(raw);
      if (err) { this._showError(err); return; }

      const code = raw.toUpperCase();
      this._clearError();
      this._busy = true;
      this._setApplyState("loading");

      try {
        const token = await this._getCartToken();
        const existingCodes = this._getAppliedCodesFromDom();
        const couponList = existingCodes.concat([code]);

        const res = await fetch("/apps/a/coupon", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "applycoupons", storefrontCoupons: couponList, cartId: token })
        });
        const data = await res.json();

        if (data.status === 400 || data.status === 500) {
          this._showError("Please enter the valid coupon code");
          return;
        }

        if (data.status === 200) {
          const cartRes = await fetch("/cart.js?v=" + Date.now());
          const cart = await cartRes.json();
          let productDiscount = false;
          (cart.items || []).forEach((item) => {
            (item.discounts || []).forEach((d) => { if (d.amount > 0) productDiscount = true; });
          });

          const discountCodes = data.data?.cart?.discountCodes || [];
          const applicable = discountCodes.filter((d) => d.applicable === true);
          const matched = applicable.find((d) => (d.code || "").toUpperCase() === code);

          if (matched) {
            const threshold = getFreeShippingThreshold(code);
            if (threshold && cart.total_price < threshold * 100) {
              const diffCents = (threshold * 100) - cart.total_price;
              this._showError(`Add ${money(diffCents)} more to your cart to qualify for free shipping with ${code}.`);
              return;
            }

            const discountTotal = this._cartLevelDiscountTotal(cart);
            const isFreeShip = /FREE|SHIP|FS/i.test(code);

            // If not a free shipping code and gives 0 discount, condition is NOT met!
            if (!isFreeShip && discountTotal <= 0) {
              let warnMsg = "";
              if (data.data?.warnings && data.data.warnings.length > 0) {
                warnMsg = this._resolveWarningMessage(data.data.warnings, applicable.length, productDiscount);
              }
              if (!warnMsg || warnMsg === "This promo code is not valid or has already expired.") {
                warnMsg = "Your cart does not meet the requirements for this promo code.";
              }
              this._showError(warnMsg);
              sessionStorage.removeItem(APPLIED_KEY);
              this._postCartUpdate({ discount: "" }).catch(() => {});
              fetch('/discount/null', { method: 'GET', credentials: 'same-origin' }).catch(() => {});
              return;
            }

            sessionStorage.setItem(APPLIED_KEY, code);
            this._serverChip?.remove();
            this._mountClientChip(code, cart);
            this._attachDiscountToCheckout(code);
            this._fireGA4(code);
            if (this._input) this._input.value = "";
            this._clearError();
            this._setFieldOpen(false);
            await this._refreshCartSection(code, true, cart);
            return;
          } else {
            sessionStorage.removeItem(APPLIED_KEY);
            this._postCartUpdate({ discount: "" }).catch(() => {});
            fetch('/discount/null', { method: 'GET', credentials: 'same-origin' }).catch(() => {});
            this._showError(this._resolveWarningMessage(data.data?.warnings || [], applicable.length, productDiscount));
          }
        }
      } catch {
        this._showError("Could not apply code. Please try again.");
      } finally {
        this._busy = false;
        this._setApplyState("idle");
      }
    }

    /* ── High-Performance In-Place Section Update (No Reload) ─────────────── */
    async _refreshCartSection(code, isApply, freshCart = null) {
      try {
        const sectionEl = document.getElementById('main-cart-items');
        const sectionId = sectionEl?.dataset.id || 'main-cart';

        const res = await fetch(`${window.location.pathname}?section_id=${sectionId}&v=${Date.now()}`);
        if (!res.ok) return;
        const htmlText = await res.text();
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');

        // Batch synchronize elements
        const syncElements = [
          '[data-cart-content] .cart',
          '.cart-message',
          '.cart-total-subtotal',
          '.cart-total-shipping:not(.cart-order-discount)',
          '.cart-total-grandtotal',
          '.cart-countdown .text-wrap',
        ];

        syncElements.forEach((sel) => {
          const incoming = doc.querySelector(sel);
          const current = document.querySelector(sel);
          if (incoming && current) {
            current.innerHTML = incoming.innerHTML;
          }
        });

        // Sync Cart Order Discount row from incoming section
        const incomingDiscount = doc.querySelector('.cart-order-discount');
        const currentDiscount = document.querySelector('.cart-order-discount');
        if (incomingDiscount && currentDiscount) {
          currentDiscount.className = "cart-total cart-order-discount";
          currentDiscount.innerHTML = incomingDiscount.innerHTML;
        } else if (incomingDiscount && !currentDiscount) {
          const newDiscountRow = incomingDiscount.cloneNode(true);
          newDiscountRow.className = "cart-total cart-order-discount";
          document.querySelector('.cart-total-subtotal')?.insertAdjacentElement('afterend', newDiscountRow);
        } else if (!incomingDiscount && currentDiscount) {
          currentDiscount.remove();
        }

        // Sync Savings Banner (insert/update/remove)
        const newSavings = doc.querySelector('.cart-total-savings-banner');
        const curSavings = document.querySelector('.cart-total-savings-banner');
        if (newSavings && curSavings) curSavings.outerHTML = newSavings.outerHTML;
        else if (newSavings && !curSavings) document.querySelector('.cart-total-grandtotal')?.insertAdjacentElement('afterend', newSavings);
        else if (!newSavings && curSavings) curSavings.remove();

        // Sync Totals and Order Discount Row from JSON cart
        let cartData = freshCart;
        if (!cartData) {
          try {
            const cartRes = await fetch("/cart.js?v=" + Date.now());
            if (cartRes.ok) cartData = await cartRes.json();
          } catch (e) { }
        }

        if (cartData) {
          this._updateTotals(cartData);
        }

        if (window.updateMobileStickyTotal) {
          window.updateMobileStickyTotal(cartData);
        }

        if (isApply && code) this._attachDiscountToCheckout(code);
        else this._detachDiscountFromCheckout();

        ['cart:updated', 'cart-update', 'cart:change', 'cart:refresh', 'discount:applied', 'discount-updated', 'cart-coupon:applied'].forEach((evt) => {
          document.dispatchEvent(new CustomEvent(evt, { bubbles: true, detail: { code: code } }));
        });
      } catch (err) {
        console.error('Error refreshing cart section:', err);
      }
    }

    async _getCartToken() {
      const res = await fetch("/cart.js");
      const data = await res.json();
      return data.token;
    }

    _getAppliedCodesFromDom() {
      const codes = [];
      const stored = sessionStorage.getItem(APPLIED_KEY);
      if (stored) codes.push(stored);
      return codes;
    }

    _resolveWarningMessage(warnings, applicableCount, productDiscount) {
      if (!warnings.length) return "This promo code is not valid or has already expired.";

      for (const w of warnings) {
        const code = w.code || "";
        const msg = w.message || "";

        if (code.includes("DISCOUNT_NO_ENTITLED_LINE_ITEMS")) {
          return productDiscount
            ? "You’ve got the best deal! Your cart already reflects the best available offer!"
            : "Enter a valid discount code";
        }
        if (code.includes("DISCOUNT_PURCHASE_NOT_IN_RANGE")) return msg;
        if (code.includes("DISCOUNT_CURRENTLY_INACTIVE") || code.includes("DISCOUNT_NOT_FOUND")) return "Enter a valid discount code";
        if (code.includes("DISCOUNT_CODE_NOT_HONOURED")) return "You’ve got the best deal! Your cart already reflects the best available offer!";
        if (msg.includes("discount code is not honoured")) {
          return msg.replace("discount code is not honoured", "").replace("The", "").trim() + "  is valid but not applicable";
        }
        if (msg.includes("This discount is not valid anymore")) return "Enter a valid discount code";
      }
      return "This promo code is not valid or has already expired.";
    }

    /* ── Remove Coupon Code ──────────────────────────────────────────────── */
    async _handleRemove(btn) {
      if (this._busy) return;
      this._busy = true;
      const oldLabel = btn?.textContent;
      if (btn) { btn.disabled = true; btn.textContent = "Removing…"; }

      try {
        sessionStorage.removeItem(APPLIED_KEY);
        sessionStorage.removeItem("as_checkout_initiated");

        const updatedCart = await this._postCartUpdate({ discount: "" }).catch(() => null);
        fetch('/discount/null', { method: 'GET', credentials: 'same-origin' }).catch(() => {});

        try {
          const token = await this._getCartToken();
          fetch("/apps/a/coupon", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ type: "applycoupons", storefrontCoupons: [], cartId: token })
          }).catch(() => {});
        } catch {}

        this._clientChip?.remove();
        this._serverChip?.remove();
        document.querySelector(".cart-order-discount")?.remove();
        this._detachDiscountFromCheckout();
        if (this._input) this._input.value = "";
        this._clearError();
        this._setFieldOpen(false);
        this._ensureSomethingVisible();
        await this._refreshCartSection("", false, updatedCart);
      } catch {
        if (btn) { btn.disabled = false; btn.textContent = oldLabel || "Remove"; }
        this._showError("Could not remove code. Please try again.");
      } finally {
        this._busy = false;
      }
    }

    _showChangeField(prefill) {
      const code = prefill
        ?? this._serverChip?.dataset.code
        ?? this._clientChip?.querySelector(".as-coupon-chip-code")?.textContent
        ?? sessionStorage.getItem(APPLIED_KEY)
        ?? "";
      this._setFieldOpen(true);
      const input = this._input;
      if (input) {
        input.value = code;
        input.focus();
        input.select();
      }
    }

    /* ── State Restoration ────────────────────────────────────────────────── */
    async _restoreState() {
      sessionStorage.removeItem("as_promo_pending");
      const fromCheckout = sessionStorage.getItem("as_checkout_initiated") === "1";
      sessionStorage.removeItem("as_checkout_initiated");

      try {
        const res = await fetch("/cart.js?v=" + Date.now());
        if (!res.ok) return;
        const cart = await res.json();

        const hasCartLevelCode = (cart.cart_level_discount_applications || []).some(
          (d) => d.type === "discount_code"
        );
        const applicableCodeObj = (cart.discount_codes || []).find((d) => d.applicable);

        // If returned from checkout and no manual discount code exists in cart, user removed it at checkout!
        if (fromCheckout && !hasCartLevelCode && !applicableCodeObj && !this._serverChip) {
          sessionStorage.removeItem(APPLIED_KEY);
          this._clientChip?.remove();
          this._serverChip?.remove();
          document.querySelector(".cart-order-discount")?.remove();
          this._detachDiscountFromCheckout();
          this._ensureSomethingVisible();
          this._updateTotals(cart);
          return;
        }

        let appliedCode = "";
        if (this._serverChip) {
          appliedCode = (this._serverChip.dataset.code ?? "").toUpperCase();
        }
        if (!appliedCode && applicableCodeObj) {
          appliedCode = (applicableCodeObj.code ?? "").toUpperCase();
        }
        if (!appliedCode) {
          const appCode = (cart.cart_level_discount_applications || []).find((d) => d.type === "discount_code");
          if (appCode) appliedCode = (appCode.title ?? "").toUpperCase();
        }
        if (!appliedCode && !fromCheckout) {
          appliedCode = (sessionStorage.getItem(APPLIED_KEY) || "").toUpperCase();
        }

        if (appliedCode) {
          const threshold = getFreeShippingThreshold(appliedCode);
          const isFreeShip = /FREE|SHIP|FS/i.test(appliedCode);
          const discountTotal = this._cartLevelDiscountTotal(cart);

          if (threshold && cart.total_price < threshold * 100) {
            // Subtotal is less than requirement -> Auto remove!
            sessionStorage.removeItem(APPLIED_KEY);
            this._postCartUpdate({ discount: "" }).catch(() => { });
            fetch('/discount/null', { method: 'GET', credentials: 'same-origin' }).catch(() => {});
            this._clientChip?.remove();
            this._serverChip?.remove();
            document.querySelector(".cart-order-discount")?.remove();
            this._detachDiscountFromCheckout();
            this._ensureSomethingVisible();
          } else if (!isFreeShip && discountTotal <= 0) {
            // Non-free-shipping code gave 0 discount: condition is NOT met -> remove!
            sessionStorage.removeItem(APPLIED_KEY);
            this._postCartUpdate({ discount: "" }).catch(() => { });
            fetch('/discount/null', { method: 'GET', credentials: 'same-origin' }).catch(() => {});
            this._clientChip?.remove();
            this._serverChip?.remove();
            document.querySelector(".cart-order-discount")?.remove();
            this._detachDiscountFromCheckout();
            this._ensureSomethingVisible();
          } else {
            sessionStorage.setItem(APPLIED_KEY, appliedCode);
            if (!this._serverChip && !this._clientChip) {
              this._mountClientChip(appliedCode, cart);
            }
            this._attachDiscountToCheckout(appliedCode);
          }
        } else {
          sessionStorage.removeItem(APPLIED_KEY);
          this._clientChip?.remove();
          this._serverChip?.remove();
          document.querySelector(".cart-order-discount")?.remove();
          this._detachDiscountFromCheckout();
          this._ensureSomethingVisible();
        }
        this._updateTotals(cart);
      } catch { /* non-blocking */ }
    }

    async _resyncFromCart() {
      try {
        const res = await fetch("/cart.js?v=" + Date.now());
        if (!res.ok) return;
        const cart = await res.json();

        const hasCartLevelCode = (cart.cart_level_discount_applications || []).some(
          (d) => d.type === "discount_code"
        );
        const applicableCodeObj = (cart.discount_codes || []).find((d) => d.applicable);
        const fromCheckout = sessionStorage.getItem("as_checkout_initiated") === "1";

        if (fromCheckout && !hasCartLevelCode && !applicableCodeObj && !this._serverChip) {
          sessionStorage.removeItem("as_checkout_initiated");
          sessionStorage.removeItem(APPLIED_KEY);
          this._clientChip?.remove();
          this._serverChip?.remove();
          document.querySelector(".cart-order-discount")?.remove();
          this._detachDiscountFromCheckout();
          this._clearError();
          this._setFieldOpen(false);
          this._ensureSomethingVisible();
          this._updateTotals(cart);
          return;
        }

        let appliedCode = "";
        if (this._serverChip) {
          appliedCode = (this._serverChip.dataset.code ?? "").toUpperCase();
        }
        if (!appliedCode && applicableCodeObj) {
          appliedCode = (applicableCodeObj.code ?? "").toUpperCase();
        }
        if (!appliedCode) {
          const appCode = (cart.cart_level_discount_applications || []).find((d) => d.type === "discount_code");
          if (appCode) appliedCode = (appCode.title ?? "").toUpperCase();
        }
        if (!appliedCode && !fromCheckout) {
          appliedCode = (sessionStorage.getItem(APPLIED_KEY) || "").toUpperCase();
        }

        if (appliedCode) {
          const threshold = getFreeShippingThreshold(appliedCode);
          const isFreeShip = /FREE|SHIP|FS/i.test(appliedCode);
          const discountTotal = this._cartLevelDiscountTotal(cart);

          if (threshold && cart.total_price < threshold * 100) {
            // Cart dropped below required amount -> Automatically remove promo code!
            sessionStorage.removeItem(APPLIED_KEY);
            this._postCartUpdate({ discount: "" }).catch(() => { });
            fetch('/discount/null', { method: 'GET', credentials: 'same-origin' }).catch(() => {});
            this._clientChip?.remove();
            this._serverChip?.remove();
            document.querySelector(".cart-order-discount")?.remove();
            this._detachDiscountFromCheckout();
            this._clearError();
            this._setFieldOpen(false);
            this._ensureSomethingVisible();
          } else if (!isFreeShip && discountTotal <= 0) {
            // Non-free-shipping code gave 0 discount: condition is NOT met -> remove!
            sessionStorage.removeItem(APPLIED_KEY);
            this._postCartUpdate({ discount: "" }).catch(() => { });
            fetch('/discount/null', { method: 'GET', credentials: 'same-origin' }).catch(() => {});
            this._clientChip?.remove();
            this._serverChip?.remove();
            document.querySelector(".cart-order-discount")?.remove();
            this._detachDiscountFromCheckout();
            this._clearError();
            this._setFieldOpen(false);
            this._ensureSomethingVisible();
          } else {
            sessionStorage.setItem(APPLIED_KEY, appliedCode);
            if (!this._serverChip && !this._clientChip) {
              this._mountClientChip(appliedCode, cart);
            }
            this._attachDiscountToCheckout(appliedCode);
          }
        } else {
          sessionStorage.removeItem(APPLIED_KEY);
          this._clientChip?.remove();
          this._serverChip?.remove();
          document.querySelector(".cart-order-discount")?.remove();
          this._detachDiscountFromCheckout();
          this._clearError();
          this._setFieldOpen(false);
          this._ensureSomethingVisible();
        }
        this._updateTotals(cart);
      } catch { /* non-blocking */ }
    }

    /* ── Client Chip ──────────────────────────────────────────────────────── */
    _mountClientChip(code, cart) {
      let chip = this._clientChip;
      if (!chip) {
        chip = document.createElement("div");
        chip.className = "as-coupon-chip as-coupon-chip--client";
        chip.dataset.clientChip = "1";
        chip.setAttribute("role", "status");
        if (this._fieldWrap) this._fieldWrap.before(chip);
        else this.querySelector(".as-coupon-section")?.prepend(chip);
      }

      const discountTotal = this._cartLevelDiscountTotal(cart);
      const isFreeShip = /FREE|SHIP|FS/i.test(code);
      const amt = isFreeShip
        ? `<span class="as-coupon-chip-label">&nbsp;— Free Shipping</span>`
        : `<span class="as-coupon-chip-amt">&nbsp;−${money(discountTotal)}</span>`;

      chip.innerHTML = `
        <div class="as-coupon-chip-left">
          <b>${this._escape(code)}</b> applied ${amt}
        </div>
        <div class="as-coupon-chip-actions">
          <button type="button" data-client-change aria-label="Change promo code">Change</button>
          <button type="button" data-client-remove aria-label="Remove promo code">Remove</button>
        </div>
      `;

      if (this._toggle) this._toggle.hidden = true;
      this._setFieldOpen(false);
    }

    /* ── Update Cart Totals & Order Discount Row ──────────────────────────── */
    _updateTotals(cart) {
      if (!cart) return;
      const appliedCode = (sessionStorage.getItem(APPLIED_KEY) || "").toUpperCase();
      const isFreeShipCode = /FREE|SHIP|FS/i.test(appliedCode);
      const threshold = getFreeShippingThreshold(appliedCode);
      const qualifiesFreeShip = isFreeShipCode && (!threshold || cart.total_price >= threshold * 100);

      // Update Shipping Row in DOM using existing theme Free Shipping design
      const shippingValEl = document.querySelector(".cart-total-shipping:not(.cart-order-discount) [data-shipping-summary-val], [data-shipping-summary-val]");
      if (shippingValEl) {
        if (qualifiesFreeShip || cart.total_price >= 8900) {
          shippingValEl.innerHTML = '<span style="text-decoration:line-through; color:#9CA3AF; font-size:14px; margin-right:6px;">$9.95</span><span style="color:#059669; font-weight:700; font-size:14px;">FREE</span>';
        } else {
          shippingValEl.innerHTML = '<span class="text">$9.95</span>';
        }
      }

      let grandTotalCents = cart.total_price;
      if (!qualifiesFreeShip && cart.total_price < 8900) {
        grandTotalCents += 995;
      }

      document.querySelectorAll(".csapps-cart-original-total, [data-sticky-total]")
        .forEach((el) => {
          const textEl = el.querySelector(".text");
          if (textEl) textEl.textContent = money(grandTotalCents);
          else el.textContent = money(grandTotalCents);
        });
      document.querySelectorAll("[data-cart-total-value]")
        .forEach((el) => el.setAttribute("data-cart-total-value", grandTotalCents));

      let row = document.querySelector(".cart-order-discount");
      const discountTotal = this._cartLevelDiscountTotal(cart);

      if (discountTotal > 0) {
        if (!row) {
          row = document.createElement("li");
          row.className = "cart-total cart-order-discount";
          row.style.cssText = "display:flex; justify-content:space-between; margin-bottom:8px; font-size:15px;";
          row.innerHTML =
            '<div class="cart-total-label"><span class="text">Order discount</span></div>' +
            '<div class="cart-total-value"><span class="text" style="color:#059669; font-weight:600;"></span></div>';
          document.querySelector(".cart-total-subtotal")?.insertAdjacentElement("afterend", row);
        }
        row.className = "cart-total cart-order-discount";
        const labelText = row.querySelector(".cart-total-label .text") || row.querySelector(".cart-total-label");
        if (labelText) labelText.textContent = "Order discount";
        const v = row.querySelector(".cart-total-value .text") || row.querySelector(".cart-total-value");
        if (v) {
          v.textContent = "−" + money(discountTotal);
          v.style.color = "#059669";
          v.style.fontWeight = "600";
        }
      } else {
        row?.remove();
      }

      // Sync Promo Code Chip Display
      const chipLeft = document.querySelector(".as-coupon-chip-left");
      if (chipLeft) {
        const codeEl = chipLeft.querySelector("b, .as-coupon-chip-code");
        const currentCode = (codeEl ? codeEl.textContent.trim() : appliedCode) || "";
        const isFreeShip = /FREE|SHIP|FS/i.test(currentCode);

        if (discountTotal > 0) {
          chipLeft.innerHTML = `<b>${this._escape(currentCode)}</b> applied <span class="as-coupon-chip-amt">−${money(discountTotal)}</span>`;
        } else if (isFreeShip) {
          chipLeft.innerHTML = `<b>${this._escape(currentCode)}</b> applied <span class="as-coupon-chip-label">&nbsp;— Free Shipping</span>`;
        } else {
          document.querySelectorAll(".as-coupon-chip").forEach((el) => el.remove());
          this._ensureSomethingVisible();
        }
      }

      // Sync TOTAL SAVINGS Green Banner (includes $9.95 free shipping savings)
      let totalSavingsCents = (cart.total_discount || 0);
      if (qualifiesFreeShip || cart.total_price >= 8900) {
        totalSavingsCents += 995;
      }

      let savingsBanner = document.querySelector(".cart-total-savings-banner");
      if (totalSavingsCents > 0) {
        if (!savingsBanner) {
          savingsBanner = document.createElement("li");
          savingsBanner.className = "cart-total-savings-banner";
          savingsBanner.style.cssText = "margin-top:10px; margin-bottom:14px;";
          document.querySelector(".cart-total-grandtotal")?.insertAdjacentElement("afterend", savingsBanner);
        }
        savingsBanner.innerHTML = `
          <div style="background:#ECFDF5; border:1px solid #A7F3D0; border-radius:8px; padding:12px 14px;">
            <div style="display:flex; justify-content:space-between; align-items:center; font-weight:700; color:#065F46; font-size:14px;">
              <div style="display:flex; align-items:center; gap:6px;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
                <span>TOTAL SAVINGS</span>
              </div>
              <span>${money(totalSavingsCents)}</span>
            </div>
          </div>
        `;
      } else {
        savingsBanner?.remove();
      }
    }

    _attachDiscountToCheckout(code) {
      document.querySelectorAll('a[href*="/checkout"], #cart-checkout, .mobile-sticky-checkout-btn').forEach((btn) => {
        try {
          const currentHref = btn.getAttribute("data-href") || btn.getAttribute("href") || "/checkout";
          const url = new URL(currentHref, window.location.origin);
          url.searchParams.set("discount", code);
          const finalUrl = url.pathname + url.search;
          if (btn.tagName.toLowerCase() === 'a') {
            btn.href = finalUrl;
          }
          btn.setAttribute("data-href", finalUrl);
          btn.dataset.asDiscountWired = "1";
        } catch { /* skip */ }
      });
    }

    _detachDiscountFromCheckout() {
      document.querySelectorAll('[data-as-discount-wired], #cart-checkout, .mobile-sticky-checkout-btn').forEach((btn) => {
        try {
          const currentHref = btn.getAttribute("data-href") || btn.getAttribute("href") || "/checkout";
          const url = new URL(currentHref, window.location.origin);
          url.searchParams.delete("discount");
          const finalUrl = url.pathname + (url.search || "");
          if (btn.tagName.toLowerCase() === 'a') {
            btn.href = finalUrl;
          }
          btn.setAttribute("data-href", finalUrl);
          delete btn.dataset.asDiscountWired;
        } catch { /* skip */ }
      });
    }

    async _postCartUpdate(body) {
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`cart/update.js ${res.status}`);
      return res.json();
    }

    _fireGA4(code) {
      window.dataLayer ??= [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "select_promotion",
        promotion_name: code,
        creative_slot: "cart_promo_field",
      });
    }

    _showError(msg) {
      const el = this._errorEl;
      if (!el) return;
      el.textContent = msg;
      el.classList.add("is-visible");
      this._input?.setAttribute("aria-invalid", "true");
      this._input?.classList.add("has-error");
    }

    _clearError() {
      const el = this._errorEl;
      if (!el) return;
      el.textContent = "";
      el.classList.remove("is-visible");
      this._input?.removeAttribute("aria-invalid");
      this._input?.classList.remove("has-error");
    }

    _setApplyState(state) {
      const btn = this._applyBtn;
      if (!btn) return;
      const loading = state === "loading";
      btn.disabled = loading;
      btn.textContent = loading ? "Applying…" : "Apply";
      btn.classList.toggle("is-loading", loading);
    }

    _escape(str) {
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }
  }

  if (!customElements.get("cart-coupon-discount")) {
    customElements.define("cart-coupon-discount", CartCouponDiscount);
  }
})();
